/**
 * 全編モーション (docs/features/brand-cut-motion-graphics-plan.md §10.5) の BGM 先行フローで、シナリオの本文ができるまで
 * タブに出す画面。BGM 生成中 → (「続けて生成」がオフ / 続行に失敗) 確認画面 → 台本生成中 の順に進む。
 *
 * - 確認画面: 試聴・「BGMを再生成」(AI がモーション専用プロンプトで作り直す。設定方法の選択には戻らない)・「このBGMでシナリオ生成」・「BGMなしでシナリオ生成」
 * - 見た目はシナリオ編集画面のカードに揃える (利用者指示 2026-10-05): BGM の段階は BGM カード (BgmSection) の体裁、
 *   台本生成中は BGM カードを出したまま、その下にモーショングラフィックス専用モードのカード (MotionScenarioPanel) の体裁で出す。
 *   ブランド設定で欠けているもの (ロゴ・ブランドカラー・デザイン言語・サウンドロゴ) は 1 行ずつ列挙する
 * - 再読み込みに耐える: シェルの行 (status='generating' + generation_metadata.motion_prepare) と、タブに控えた BGM ジョブ id から
 *   段階を復元する。本文ができたら (status が generating でなくなったら) onReady で読み込む
 *   (このタブのシェルフローがポーリング中なら、そちらが読み込むので呼ばない)。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './ScenarioGenerator.module.css';
import { LoadingOverlay } from '../../shared/LoadingOverlay';
import { useToast } from '../../../hooks/useToast';
import { getErrorMessage } from '../../../utils/getErrorMessage';
import { getJobStatus } from '../../../services/asyncJobApi';
import { fetchMaterialById, fetchMaterialSignedUrl } from '../../../features/materials/api';
import { playExclusive } from '../../../utils/exclusiveAudio';
import { GENERATED_BGM_LABEL, resolveBgmDisplayName, type BgmDisplayNameSource } from './bgmDisplayName';
import {
  generateMotionScenarioFromBgm,
  getScenario,
  regenerateMotionBgm,
  type BgmProvider,
  type MotionSongSummary,
} from '../../../features/scenarios/api';
import {
  deriveMotionPrepareStage,
  forgetMotionBgmJob,
  formatMotionSongSummary,
  motionWholeBgmTooLongNotice,
  readMotionPrepare,
  recallMotionBgmJob,
  rememberMotionBgmJob,
  type MotionBgmJobView,
} from './motionScenario';
import type { Scenario } from './types';
import { useScenarioMotionBrandGaps } from './hooks/useScenarioMotionBrandGaps';
import { useGenerationJobsSafe } from '../state/jobsContext';

const POLL_MS = 4000;
/** 生成キューの行の段階に出す文言 (BGM 先行フローの BGM 生成中) */
export const BGM_GENERATING_STAGE_LABEL = 'BGMを生成中';

type RowView = {
  status: string | null;
  generation_metadata: Record<string, unknown> | null;
  bgm_material_id: string | null;
  bgm_excluded: boolean;
};

type BgmJobState = MotionBgmJobView & {
  error?: string | null;
  continueError?: string | null;
  previewUrl?: string | null;
  song?: MotionSongSummary | null;
  /** 解析サービスの失敗 (拍が取れない曲とは別) */
  songError?: string | null;
  materialId?: string | null;
};

function readStoredBgmProvider(): BgmProvider | undefined {
  try {
    const v = localStorage.getItem('bgm_provider_last');
    if (v === 'elevenlabs' || v === 'lyria' || v === 'lyria-pro') return v;
  } catch { /* ignore */ }
  return undefined;
}

export function MotionPrepareStagePanel({
  scenario,
  isPolledElsewhere,
  onReady,
}: {
  scenario: Scenario;
  /** このタブのシェルフローが本文待ちでポーリングしているか (していれば本文の読み込みはそちらに任せる) */
  isPolledElsewhere: (scenarioId: string) => boolean;
  /** 本文ができた (status が generating でなくなった) ときに呼ぶ */
  onReady: (scenarioId: string) => void;
}) {
  const toast = useToast();
  const scenarioId = scenario.id;
  const [row, setRow] = useState<RowView>(() => ({
    status: scenario.status ?? 'generating',
    generation_metadata: (scenario.generation_metadata as Record<string, unknown> | undefined) ?? null,
    bgm_material_id: scenario.bgm_material_id ?? null,
    bgm_excluded: !!scenario.bgm_excluded,
  }));
  const [bgmJobId, setBgmJobId] = useState<string | null>(() => recallMotionBgmJob(scenarioId));
  const [bgmJob, setBgmJob] = useState<BgmJobState | null>(null);
  const [localScenarioJobId, setLocalScenarioJobId] = useState<string | null>(null);
  const [localSong, setLocalSong] = useState<MotionSongSummary | null | undefined>(undefined);
  const [localSongError, setLocalSongError] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'retry' | 'generate' | 'without'>(null);
  const [failed, setFailed] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [bgmName, setBgmName] = useState<string | null>(null);
  const [bgmPlaying, setBgmPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // 試聴プレーヤーが読んだ BGM の実尺 (曲の要約がまだ無いときの 60 秒超の判定に使う)
  const [audioDurationSec, setAudioDurationSec] = useState<number | null>(null);
  const readyFiredRef = useRef(false);
  // ブランド設定で欠けているもの (§10.10.4): 生成は止めず代替で作るので、何が欠けてどう作るかを先に知らせる
  const brandGaps = useScenarioMotionBrandGaps(scenarioId);
  const brandGapsNotice = brandGaps.length > 0 ? (
    <ul className={styles.motionPrepareGaps} data-testid="motion-prepare-brand-gaps">
      {brandGaps.map((g) => <li key={g}>{g}</li>)}
    </ul>
  ) : null;

  const jobs = useGenerationJobsSafe();
  const prep = readMotionPrepare(row);
  const stage = deriveMotionPrepareStage({
    bgmMode: prep?.bgmMode ?? null,
    scenarioJobId: localScenarioJobId ?? prep?.scenarioJobId,
    bgmJob,
    bgmMaterialId: row.bgm_material_id,
    bgmExcluded: row.bgm_excluded,
  });

  // BGM ができて利用者の操作待ち (確認画面 / 失敗) の間は、このシェルの生成キューの行を paused にして
  // ステータスバー・生成キュー・タブの虹色表示を止める。台本生成が始まったら戻す (利用者指示 2026-10-05)
  // BGM 生成中は生成キューの行の段階に「BGMを生成中」を出す (行のラベルは「シナリオを生成中...」のまま)
  const setActiveJobs = jobs?.setActiveJobs;
  useEffect(() => {
    if (!setActiveJobs) return;
    const paused = stage === 'confirm' || stage === 'bgm_failed';
    const stageLabel = stage === 'bgm_generating' ? BGM_GENERATING_STAGE_LABEL : undefined;
    setActiveJobs((prev) => {
      let changed = false;
      const next = prev.map((j) => {
        if (j.type !== 'scenario' || j.scenarioId !== scenarioId) return j;
        // 段階の文言は自分が付けたものだけ外す (backend の段階通知を消さない)
        const nextStage = stageLabel ?? (j.stage === BGM_GENERATING_STAGE_LABEL ? undefined : j.stage);
        if (!!j.paused === paused && j.stage === nextStage) return j;
        changed = true;
        const { stage: _omit, ...rest } = j;
        return { ...rest, paused, ...(nextStage !== undefined ? { stage: nextStage } : {}) };
      });
      return changed ? next : prev;
    });
  }, [stage, scenarioId, setActiveJobs]);

  // シェルの行と BGM ジョブのポーリング
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const full = await getScenario(scenarioId);
        if (cancelled) return;
        const next: RowView = {
          status: (full as { status?: string | null }).status ?? null,
          generation_metadata: (full.generation_metadata as Record<string, unknown> | undefined) ?? null,
          bgm_material_id: (full as { bgm_material_id?: string | null }).bgm_material_id ?? null,
          bgm_excluded: !!(full as { bgm_excluded?: boolean | null }).bgm_excluded,
        };
        setRow(next);
        if (next.status === 'failed') { setFailed(true); return; }
        if (next.status !== 'generating' && !readyFiredRef.current) {
          readyFiredRef.current = true;
          forgetMotionBgmJob(scenarioId);
          if (!isPolledElsewhere(scenarioId)) onReady(scenarioId);
          return;
        }
      } catch (err) {
        if ((err as { response?: { status?: number } })?.response?.status === 404) {
          if (!cancelled) setFailed(true);
          return;
        }
      }
      if (bgmJobId && !(bgmJob && ['completed', 'failed', 'cancelled'].includes(bgmJob.status))) {
        try {
          const job = await getJobStatus(bgmJobId);
          if (cancelled) return;
          const result = (job.result_data ?? {}) as Record<string, unknown>;
          setBgmJob({
            status: job.status,
            scenarioJobId: typeof result.scenario_job_id === 'string' ? result.scenario_job_id : null,
            error: job.error_message ?? null,
            continueError: typeof result.motion_continue_error === 'string' ? result.motion_continue_error : null,
            previewUrl: typeof result.preview_url === 'string' ? result.preview_url : null,
            song: 'song' in result ? (result.song as MotionSongSummary | null) : undefined,
            songError: typeof result.song_error === 'string' && result.song_error ? result.song_error : null,
            materialId: typeof result.material_id === 'string' ? result.material_id : null,
          });
        } catch { /* 次の周期で取り直す */ }
      }
    };
    void tick();
    const timer = window.setInterval(() => { void tick(); }, POLL_MS);
    return () => { cancelled = true; window.clearInterval(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scenarioId, bgmJobId, bgmJob?.status]);

  // 試聴 URL (BGM ジョブの preview_url > 紐づいた素材の署名 URL)
  const bgmMaterialId = bgmJob?.materialId ?? row.bgm_material_id;
  useEffect(() => {
    // 確認画面と台本生成中 (BGM カードを出したままにする) で試聴できるようにする
    if (stage !== 'confirm' && stage !== 'scenario_generating') return;
    if (bgmJob?.previewUrl) { setAudioUrl(bgmJob.previewUrl); return; }
    if (!bgmMaterialId) { setAudioUrl(null); return; }
    let cancelled = false;
    fetchMaterialSignedUrl(bgmMaterialId, true)
      .catch(() => fetchMaterialSignedUrl(bgmMaterialId, false))
      .then((url) => { if (!cancelled) setAudioUrl(url); })
      .catch(() => { if (!cancelled) setAudioUrl(null); });
    return () => { cancelled = true; };
  }, [stage, bgmMaterialId, bgmJob?.previewUrl]);

  // 見出しに出す曲名 (シナリオ編集画面の BGM カードと同じ解決・BGM (自動生成) にフォールバック)
  useEffect(() => {
    if (!bgmMaterialId) { setBgmName(null); return; }
    let cancelled = false;
    fetchMaterialById(bgmMaterialId)
      .then((m) => { if (!cancelled) setBgmName(resolveBgmDisplayName(m as BgmDisplayNameSource | null) ?? GENERATED_BGM_LABEL); })
      .catch(() => { if (!cancelled) setBgmName(GENERATED_BGM_LABEL); });
    return () => { cancelled = true; };
  }, [bgmMaterialId]);
  // 試聴 URL が変わったら再生状態を戻す
  useEffect(() => { setBgmPlaying(false); }, [audioUrl]);

  const song = useMemo<MotionSongSummary | null | undefined>(() => {
    if (localSong !== undefined) return localSong;
    if (bgmJob?.song !== undefined) return bgmJob.song;
    return prep?.song;
  }, [localSong, bgmJob?.song, prep?.song]);
  // 解析サービスの失敗 (拍が取れない曲とは別)。song が無いときの文言を区別する
  const songError = localSongError ?? bgmJob?.songError ?? prep?.songError ?? null;
  // 全編を 1 本で作れるのは 60 秒まで (§10.6)。曲が長ければ注意する (生成は止めない)
  const bgmTooLongNotice = motionWholeBgmTooLongNotice(song?.songSec ?? audioDurationSec);

  const handleRetry = async () => {
    setBusy('retry');
    try {
      const jobId = await regenerateMotionBgm(scenarioId, readStoredBgmProvider());
      rememberMotionBgmJob(scenarioId, jobId);
      setBgmJob({ status: 'pending' });
      setBgmJobId(jobId);
      setAudioUrl(null);
      setAudioDurationSec(null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'BGM のやり直しを受け付けられませんでした'));
    } finally {
      setBusy(null);
    }
  };

  const handleGenerate = async (withoutBgm: boolean) => {
    setBusy(withoutBgm ? 'without' : 'generate');
    try {
      const res = await generateMotionScenarioFromBgm(scenarioId, { withoutBgm });
      setLocalScenarioJobId(res.scenario_job_id);
      setLocalSong(withoutBgm ? null : res.song ?? null);
      setLocalSongError(withoutBgm ? null : res.song_error ?? null);
      forgetMotionBgmJob(scenarioId);
      if (!withoutBgm && !res.alreadyStarted) toast.show(formatMotionSongSummary(res.song ?? null, res.song_error ?? null));
    } catch (err) {
      toast.error(getErrorMessage(err, 'シナリオの生成を受け付けられませんでした'));
    } finally {
      setBusy(null);
    }
  };

  const noteStyle: React.CSSProperties = { fontSize: 13, color: 'rgba(245, 241, 230, 0.7)', lineHeight: 1.6 };
  const hasBgm = prep?.bgmMode !== 'none';
  // 見出しの薄い補足: 生成中 / 失敗はその旨、BGM があるときは曲名 (シナリオ編集画面の BGM カードと同じ)
  const bgmSubtitle =
    stage === 'bgm_generating' ? '生成中…'
      : stage === 'bgm_failed' ? '作れませんでした'
        : (bgmName ?? (bgmMaterialId ? GENERATED_BGM_LABEL : undefined));

  if (failed) {
    return (
      <div className={styles.motionPrepareStack}>
        <StageCard title="シナリオ">
          <strong>シナリオを作れませんでした</strong>
          <span style={noteStyle}>生成に失敗したか、削除されました。タブを閉じてやり直してください。</span>
        </StageCard>
      </div>
    );
  }

  // 隠した audio + フッターの再生 / 停止ボタン (シナリオ編集画面の BGM カードのフッターと同じ)
  const hiddenAudio = audioUrl ? (
    <audio
      ref={(el) => { audioRef.current = el; }}
      src={audioUrl}
      preload="metadata"
      style={{ display: 'none' }}
      data-testid="motion-prepare-bgm-audio"
      onPlay={() => setBgmPlaying(true)}
      onPause={() => setBgmPlaying(false)}
      onEnded={() => setBgmPlaying(false)}
      onLoadedMetadata={(e) => {
        const d = e.currentTarget.duration;
        setAudioDurationSec(Number.isFinite(d) ? d : null);
      }}
    />
  ) : null;
  const playButton = (
    <button
      type="button"
      disabled={!audioUrl}
      onClick={() => {
        if (!audioRef.current || !audioUrl) return;
        if (bgmPlaying) audioRef.current.pause();
        else void playExclusive(audioRef.current).catch(() => setBgmPlaying(false));
      }}
      title={!audioUrl ? '試聴の準備中…' : bgmPlaying ? '停止' : 'BGMを再生'}
      aria-label={bgmPlaying ? '停止' : 'BGMを再生'}
      style={{ border: 'none', background: 'transparent', cursor: audioUrl ? 'pointer' : 'default', padding: 0, display: 'inline-flex', alignItems: 'center', opacity: audioUrl ? 1 : 0.35 }}
    >
      <img src={bgmPlaying ? '/button_stop_orange.svg' : '/button_play_orange.svg'} alt="" style={{ width: 24, height: 24 }} />
    </button>
  );

  if (stage === 'scenario_generating') {
    // BGM カードは出したまま (曲名・試聴・曲の要約)、その下に専用モードのカードで台本生成中を出す
    return (
      <div className={styles.motionPrepareStack}>
        {hasBgm && (
          <StageCard
            title="BGM"
            subtitle={bgmSubtitle}
            testId="motion-prepare-bgm-card"
            footer={bgmMaterialId ? <div className={styles.motionPrepareFooterLeft}>{playButton}</div> : undefined}
          >
            {hiddenAudio}
            {song !== undefined && <span style={noteStyle}>{formatMotionSongSummary(song, songError)}</span>}
            {bgmTooLongNotice && <span className={styles.motionSyncStaleNotice}>{bgmTooLongNotice}</span>}
          </StageCard>
        )}
        <StageCard title="モーショングラフィックス専用モード" testId="motion-prepare-motion-card">
          <div className={styles.motionPrepareLoading}>
            <LoadingOverlay message="台本を生成中…" transparent />
          </div>
          {brandGapsNotice}
        </StageCard>
      </div>
    );
  }

  if (stage === 'bgm_generating') {
    return (
      <div className={styles.motionPrepareStack}>
        <StageCard title="BGM" subtitle={bgmSubtitle} testId="motion-prepare-bgm-card">
          <div className={styles.motionPrepareLoading}>
            <LoadingOverlay message="BGM を生成中…" transparent />
          </div>
          {prep?.autoContinue !== false && (
            <span style={noteStyle}>BGM ができたら、曲を解析して続けてシナリオを生成します</span>
          )}
          {brandGapsNotice}
        </StageCard>
      </div>
    );
  }

  const disabled = busy !== null;
  const retryButton = (
    <button
      type="button"
      // 非ホバー時は枠線と塗り無し・ホバーで薄い塗り (既存の btn-toggle・利用者指示 2026-10-05)
      className="btn-toggle"
      disabled={disabled}
      title="AIがモーション向けのプロンプトでBGMを作り直します (BGMのポイントを消費します)。設定方法を選ぶ画面には戻りません"
      onClick={() => void handleRetry()}
    >
      <img src="/refresh.svg" alt="" className={`btn-toggle-icon ${styles.motionPrepareRetryIcon} ${busy === 'retry' ? styles.spinning : ''}`} />
      {busy === 'retry' ? '送信中…' : 'BGMを再生成'}
    </button>
  );
  const footer = (
    <>
      <div className={styles.motionPrepareFooterLeft}>
        {stage === 'confirm' && playButton}
        {retryButton}
      </div>
      <div className={styles.motionPrepareFooterRight}>
        <button type="button" className="btn-toggle" disabled={disabled} onClick={() => void handleGenerate(true)}>
          {busy === 'without' ? '送信中…' : 'BGMなしでシナリオ生成'}
        </button>
        {stage === 'confirm' && (
          <button
            type="button"
            className={`${styles.bgmGenerateButton} ${styles.motionPrepareGenerateSub} ${busy === 'generate' ? 'ai-loading-active' : ''}`}
            disabled={disabled}
            onClick={() => void handleGenerate(false)}
          >
            {busy === 'generate' ? '送信中…' : 'このBGMでシナリオ生成'}
          </button>
        )}
      </div>
    </>
  );
  return (
    <div className={styles.motionPrepareStack}>
      <StageCard title="BGM" subtitle={bgmSubtitle} testId="motion-prepare-bgm-card" footer={footer}>
        {stage === 'bgm_failed' ? (
          <>
            {bgmJob?.error && <span className={styles.motionSyncStaleNotice}>{bgmJob.error}</span>}
            <span style={noteStyle}>BGMを再生成するか、BGMなしでシナリオを生成してください (BGMなしは台本を秒ベースで書きます)。</span>
          </>
        ) : (
          <>
            {hiddenAudio}
            {bgmJob?.continueError && (
              <span className={styles.motionSyncStaleNotice}>
                続けてシナリオを生成できませんでした ({bgmJob.continueError})。下のボタンから進めてください
              </span>
            )}
            <span style={noteStyle}>
              {song !== undefined
                ? formatMotionSongSummary(song, songError)
                : '曲のテンポや小節を分析して、曲に合わせたシナリオを生成します。'}
            </span>
            {bgmTooLongNotice && <span className={styles.motionSyncStaleNotice}>{bgmTooLongNotice}</span>}
          </>
        )}
        {brandGapsNotice}
      </StageCard>
    </div>
  );
}

/**
 * シナリオ編集画面のカード (BgmSection / MotionScenarioPanel の flowStepSection + stepHeader) と同じ体裁の箱。
 * 折りたたみはしない (生成待ちの画面なので常に開いた状態)。見出し行は三角 (開いた向き) + 見出し + 薄い補足 (曲名等)。
 * footer を渡すと BGM カードのフッター (stepResultArea: 再生ボタン・操作ボタンの全幅バー) を本体の下に出す。
 */
function StageCard({ title, subtitle, testId, footer, children }: { title: string; subtitle?: string; testId?: string; footer?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className={styles.flowStepSection} data-testid={testId}>
      <div className={styles.stepHeader}>
        <div className={styles.stepTitle}>
          <span className={styles.expandTriangle} style={{ transform: 'rotate(90deg)' }}>
            <svg width="12" height="12" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 1l4 3-4 3" /></svg>
          </span>
          <span style={{ whiteSpace: 'nowrap' }}>{title}</span>
          {subtitle && (
            <span style={{ fontWeight: 400, color: 'rgba(245, 241, 230, 0.4)', marginLeft: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{subtitle}</span>
          )}
        </div>
      </div>
      <div className={styles.bgmBody} style={{ gap: 10, paddingTop: 4, paddingBottom: footer ? 6 : 14 }}>
        {children}
      </div>
      {footer && (
        <div className={styles.stepResultArea} style={{ justifyContent: 'space-between' }}>
          {footer}
        </div>
      )}
    </div>
  );
}
